import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home as HomeIcon, Building, Users, TrendingUp, Award, Heart, Bed, Bath, Square, MapPin } from 'lucide-react';
import BookingModal from '@/components/BookingModal';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';
import GoogleReviewsBar from '@/components/GoogleReviewsBar';
import api from '@/utils/api';

const Home = () => {
  const navigate = useNavigate();
  const [showBooking, setShowBooking] = useState(false);
  const [mlsProperties, setMlsProperties] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingMls, setLoadingMls] = useState(true);

  useEffect(() => {
    loadFeaturedContent();
  }, []);

  const loadFeaturedContent = async () => {
    try {
      // Fetch live MLS listings from Memphis, Collierville, Germantown - $350k+ 
      // SimplyRETS requires separate calls for each city
      const cities = ['Memphis', 'Collierville', 'Germantown'];
      const priceParams = 'minprice=350000&status=Active&sort=-listdate&limit=3';
      
      const [cityResults, blogRes] = await Promise.all([
        Promise.all(cities.map(city => 
          api.get(`/mls/properties?cities=${city}&${priceParams}`)
        )),
        api.get('/blog?limit=3')
      ]);
      
      // Combine all city results, filter out rentals, and sort by list date (newest first)
      const allProperties = cityResults
        .flatMap(res => res.data.properties || [])
        .filter(p => p.type !== 'Rental' && p.type !== 'RNT')  // Exclude rentals
        .sort((a, b) => new Date(b.listDate) - new Date(a.listDate))
        .slice(0, 6);
      
      setMlsProperties(allProperties);
      setBlogPosts(blogRes.data);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoadingMls(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Video */}
      <SEO 
        title="Luxury Homes & Property Management in Memphis, TN & North MS"
        description="Looking for a home in Memphis or North Mississippi? Bespoke Realty is a Black-owned, woman-led brokerage offering personalized real estate services. View listings today!"
        keywords="Memphis real estate, homes for sale Memphis TN, Southaven MS homes, Olive Branch real estate, Black-owned real estate, woman-led brokerage, property management Memphis"
      />
      <StructuredData schema={[schemas.organization(), schemas.localBusiness()]} />
      <section className="relative h-[85vh] flex items-end justify-center overflow-hidden pb-24 sm:pb-28">
        {/* Charcoal Overlay - darker for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/60 via-[#1a1a1a]/50 to-[#1a1a1a]/90 z-10"></div>
        
        {/* Video Background */}
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: 'center' }}
        >
          <source src="https://customer-assets-wrfwihn1.emergentagent.net/job_5290d281-24fe-4df4-8f7b-f48fa4c792c6/artifacts/a177egd9_Untitled%20design%2824%29.mp4" type="video/mp4" />
        </video>
        
        <div className="relative z-20 text-center text-white px-4 sm:px-6 max-w-5xl mx-auto">
          {/* Headline */}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4"
            data-testid="hero-headline"
          >
            Memphis & North Mississippi<br className="hidden sm:block" /> Real Estate, Done Differently.
          </h1>

          {/* Subhead */}
          <p
            className="text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed"
            data-testid="hero-subhead"
          >
            From first homes to investment properties — our team serves TN and MS with local expertise, honest guidance, and the personal attention families deserve.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-[#BCA480] hover:bg-[#a89470] text-white px-6 sm:px-8 w-full sm:w-auto"
              data-testid="buy-home-cta"
            >
              <Link to="/mls">Search Homes</Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="bg-white text-[#434345] hover:bg-gray-100 border-white px-6 sm:px-8 w-full sm:w-auto"
              data-testid="sell-home-cta"
            >
              <Link to="/home-valuation">Get Your Home's Value</Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 px-6 sm:px-8 w-full sm:w-auto"
              onClick={() => setShowBooking(true)}
              data-testid="book-consultation-btn"
            >
              Book Consultation
            </Button>
          </div>

          {/* Trust chips */}
          <div
            className="flex flex-wrap gap-4 sm:gap-6 justify-center items-center mt-10 text-[11px] sm:text-xs text-white/75"
            data-testid="hero-trust-chips"
          >
            <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#BCA480]"></span>Licensed in TN, MS &amp; AR</span>
            <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#BCA480]"></span>Live MLS listings</span>
            <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#BCA480]"></span>Full-service brokerage</span>
          </div>
        </div>

        {/* Bottom-anchored brand pill */}
        <div
          className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-xs sm:text-sm font-medium tracking-wide text-white/90 whitespace-nowrap"
          data-testid="hero-brand-pill"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#BCA480]"></span>
          Boutique · Black-owned · Woman-led · Family-owned
        </div>
      </section>

      {/* Google Reviews Trust Bar */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <GoogleReviewsBar />
        </div>
      </section>

      {/* Quick Action Cards */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 sm:mb-12" data-testid="services-heading">How Can We Serve You?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <Link to="/buy" className="group" data-testid="buy-card">
              <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-all h-full">
                <HomeIcon className="h-12 w-12 text-[#BCA480] mb-4" />
                <h3 className="text-xl font-bold mb-3 group-hover:text-[#BCA480] transition-colors">Buy a Home</h3>
                <p className="text-gray-600">Find your dream home with our expert guidance and local market knowledge.</p>
              </div>
            </Link>

            <Link to="/sell" className="group" data-testid="sell-card">
              <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-all h-full">
                <TrendingUp className="h-12 w-12 text-[#BCA480] mb-4" />
                <h3 className="text-xl font-bold mb-3 group-hover:text-[#BCA480] transition-colors">Sell a Home</h3>
                <p className="text-gray-600">Get the best value for your property with our strategic marketing approach.</p>
              </div>
            </Link>

            <Link to="/property-management" className="group" data-testid="property-mgmt-card">
              <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-all h-full">
                <Building className="h-12 w-12 text-[#BCA480] mb-4" />
                <h3 className="text-xl font-bold mb-3 group-hover:text-[#BCA480] transition-colors">Property Management</h3>
                <p className="text-gray-600">Professional management services to maximize your investment returns.</p>
              </div>
            </Link>

            <Link to="/apply" className="group" data-testid="join-team-card">
              <div className="bg-gray-50 p-8 rounded-lg shadow-md hover:shadow-xl transition-all h-full">
                <Users className="h-12 w-12 text-[#BCA480] mb-4" />
                <h3 className="text-xl font-bold mb-3 group-hover:text-[#BCA480] transition-colors">Join Our Team</h3>
                <p className="text-gray-600">Grow your real estate career with a brokerage that values you.</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Just Listed Properties - Live MLS Listings */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8 sm:mb-12 gap-4">
            <h2 className="text-3xl sm:text-4xl font-bold">Just Listed</h2>
            <Button asChild variant="outline" className="border-[#BCA480] text-[#BCA480] hover:bg-[#BCA480] hover:text-white">
              <Link to="/mls">View All Listings</Link>
            </Button>
          </div>
          
          {loadingMls ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BCA480]"></div>
              <span className="ml-3 text-gray-600">Loading listings...</span>
            </div>
          ) : mlsProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mlsProperties.slice(0, 6).map((property) => (
                <div 
                  key={property.mlsId} 
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/mls/${property.mlsId}`)}
                  data-testid={`featured-property-${property.mlsId}`}
                >
                  <div className="relative h-56 bg-gray-200 overflow-hidden">
                    {property.primaryPhoto ? (
                      <img 
                        src={property.primaryPhoto} 
                        alt={property.fullAddress}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <span>No Photo</span>
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <span className="absolute top-3 left-3 px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded">
                      {property.status || 'Active'}
                    </span>
                    
                    {/* Days on Market */}
                    {property.daysOnMarket !== undefined && property.daysOnMarket <= 7 && (
                      <span className="absolute top-3 right-3 px-2 py-1 bg-[#BCA480] text-white text-xs font-semibold rounded">
                        New
                      </span>
                    )}
                  </div>
                  
                  <div className="p-5">
                    <p className="text-2xl font-bold text-[#BCA480] mb-2">
                      {formatPrice(property.listPrice)}
                    </p>
                    
                    <div className="flex items-start text-gray-600 text-sm mb-3">
                      <MapPin className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0 text-[#BCA480]" />
                      <span className="line-clamp-2">{property.fullAddress}</span>
                    </div>
                    
                    <div className="flex gap-4 text-gray-600 text-sm mb-4">
                      <div className="flex items-center">
                        <Bed className="h-4 w-4 mr-1" />
                        <span>{property.bedrooms} bd</span>
                      </div>
                      <div className="flex items-center">
                        <Bath className="h-4 w-4 mr-1" />
                        <span>{property.bathrooms} ba</span>
                      </div>
                      {property.area > 0 && (
                        <div className="flex items-center">
                          <Square className="h-4 w-4 mr-1" />
                          <span>{property.area.toLocaleString()} sqft</span>
                        </div>
                      )}
                    </div>
                    
                    <Button className="w-full bg-[#BCA480] hover:bg-[#a89470]">
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No listings available at the moment.</p>
              <Button asChild className="bg-[#BCA480] hover:bg-[#a89470]">
                <Link to="/mls">Browse All Properties</Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Featured Areas */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 sm:mb-12">Featured Areas We Serve</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {[
              { name: 'Memphis', slug: '/memphis-tn-real-estate', image: 'https://images.unsplash.com/photo-1570698473651-b2de99bae12f?crop=entropy&cs=srgb&fm=jpg&q=85' },
              { name: 'Southaven', slug: '/southaven-ms-real-estate', image: 'https://images.pexels.com/photos/34689484/pexels-photo-34689484.jpeg' },
              { name: 'Olive Branch', slug: '/olive-branch-ms-real-estate', image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&q=85' },
              { name: 'Collierville', slug: '/collierville-tn-real-estate', image: 'https://images.unsplash.com/photo-1544984243-ec57ea16fe25?crop=entropy&cs=srgb&fm=jpg&q=85' },
              { name: 'Germantown', slug: '/germantown-tn-real-estate', image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85' },
              { name: 'Hernando', slug: '/hernando-ms-real-estate', image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?crop=entropy&cs=srgb&fm=jpg&q=85' }
            ].map((area) => (
              <Link 
                key={area.name} 
                to={area.slug}
                className="group relative h-48 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20 z-10"></div>
                <img 
                  src={area.image} 
                  alt={area.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 z-20 flex items-end p-4">
                  <h3 className="text-white font-bold text-lg">{area.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Beau the Bespoke Hummingbird */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#BCA480]/10 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 sm:gap-12">
            <div className="md:w-1/2">
              <img 
                src="https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/0469dy7q_Beau.png"
                alt="Beau, Bespoke Realty's hummingbird mascot with compass pendant"
                className="w-full max-w-md mx-auto"
              />
            </div>
            <div className="md:w-1/2 text-center md:text-left">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">Beau — The Spirit of Bespoke</h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                Grace in motion. Precision in every detail. Beau represents our commitment to move with purpose and tailor every experience to you.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8">
                Look for Beau guiding you through helpful tips, milestones, and celebrations across our site and events.
              </p>
              <Button 
                asChild 
                className="bg-[#BCA480] hover:bg-[#a89470]"
              >
                <Link to="/about#beau">Meet Beau</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#434345] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">What Our Clients Say</h2>
            <div className="flex items-center justify-center gap-1 mb-2">
              <span className="text-yellow-400 text-2xl">★★★★★</span>
            </div>
            <a 
              href="https://g.page/r/CS98Z87CEL2XEAE/review" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#BCA480] hover:underline text-sm"
            >
              Leave us a review on Google →
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
              <div className="flex gap-1 mb-3 text-yellow-400">★★★★★</div>
              <p className="text-base mb-4 italic leading-relaxed">"Working with Bespoke Realty was an absolute pleasure. They were incredibly knowledgeable and guided us through every step in the selling process gracefully. Communication was always prompt and clear. I highly recommend to anyone looking to buy or sell."</p>
              <p className="font-bold text-[#BCA480]">— Lorrie Gillespie</p>
            </div>
            <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
              <div className="flex gap-1 mb-3 text-yellow-400">★★★★★</div>
              <p className="text-base mb-4 italic leading-relaxed">"Truly heaven sent… this was my 1st time buying a home and I don't think I would have made it without Telesa. She was very professional and knowledgeable.. she listened to my needs and executed them.. I couldn't have asked for a better realtor!"</p>
              <p className="font-bold text-[#BCA480]">— Keneetha Jeffries</p>
            </div>
            <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
              <div className="flex gap-1 mb-3 text-yellow-400">★★★★★</div>
              <p className="text-base mb-4 italic leading-relaxed">"I love love love Benjamin & Telesa.. the best realtors I've ever encountered. I will refer them over and over again! So personable and professional! They're like my extended friends/family."</p>
              <p className="font-bold text-[#BCA480]">— Diarah Humphreys</p>
            </div>
            <div className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
              <div className="flex gap-1 mb-3 text-yellow-400">★★★★★</div>
              <p className="text-base mb-4 italic leading-relaxed">"Working with Telesa was a great pleasure. She was very professional from start to finish. She listened to my wants/needs and helped me find the perfect home. I would definitely use her services again!"</p>
              <p className="font-bold text-[#BCA480]">— Jameika Agnew</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Bespoke Realty */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">About Bespoke Realty</h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              At Bespoke Realty, we believe every client deserves a personalized experience. 
              As a Black-owned, woman-led brokerage, we bring excellence, faith, and leadership 
              to every transaction. Our team is dedicated to serving Mississippi, Tennessee, and Arkansas 
              with integrity, professionalism, and a commitment to your success.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="inline-block p-3 sm:p-4 bg-[#BCA480]/10 rounded-full mb-3 sm:mb-4">
                <Award className="h-8 w-8 sm:h-10 sm:w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Excellence</h3>
              <p className="text-gray-600">We maintain the highest standards in every transaction and interaction.</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-3 sm:p-4 bg-[#BCA480]/10 rounded-full mb-3 sm:mb-4">
                <Heart className="h-8 w-8 sm:h-10 sm:w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Personalized Service</h3>
              <p className="text-gray-600">Your unique needs and goals drive our tailored approach.</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-3 sm:p-4 bg-[#BCA480]/10 rounded-full mb-3 sm:mb-4">
                <Users className="h-8 w-8 sm:h-10 sm:w-10 text-[#BCA480]" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Community Focus</h3>
              <p className="text-gray-600">We're committed to serving and strengthening our local communities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#BCA480] to-[#a89470] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">Ready to Get Started?</h2>
          <p className="text-lg sm:text-xl mb-6 sm:mb-8">Let's discuss how we can help you achieve your real estate goals.</p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-[#434345] hover:bg-gray-100 px-6 sm:px-8 w-full sm:w-auto"
              onClick={() => setShowBooking(true)}
              data-testid="cta-book-btn"
            >
              Schedule a Consultation
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 px-6 sm:px-8 w-full sm:w-auto"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Emergent Badge Footer - Stationary at bottom */}
      <div className="bg-white border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center items-center">
          <a
            href="https://app.emergent.sh/?utm_source=emergent-badge"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-gray-600 hover:text-[#BCA480] transition-colors text-sm"
          >
            <img
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
              alt="Emergent"
              className="w-5 h-5"
            />
            <span>Made with Emergent</span>
          </a>
        </div>
      </div>

      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />
    </div>
  );
};

export default Home;
